import { chunkBreakables, chunkHazards, OBSTACLE_CHUNK, type GameState, type Hazard } from '@gra/engine';
import { CircleGeometry, Color, Group, Mesh, ShaderMaterial, Vector3, type Object3D, type Scene } from 'three';
import type { Assets } from './assets';
import type { Particles } from './particles';
import { HAZARD_LOOKS, WORLD_SCALE, type BreakableProp, type ZoneLook } from './visuals';

const S = WORLD_SCALE;
/** Ile kawałków mapy wokół bohatera trzymamy w scenie (w każdą stronę). */
const VIEW_CHUNKS = 2;

const hazardVertex = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Nieregularna plama z jaśniejszym brzegiem i wolno falującą powierzchnią.
const hazardFragment = /* glsl */ `
  uniform vec3 uColor;
  uniform vec3 uEdge;
  uniform float uTime;
  uniform float uSeed;
  uniform float uGlow;
  varying vec2 vUv;
  void main() {
    float a = atan(vUv.y, vUv.x);
    float r = length(vUv);
    float wobble = 0.08 * sin(a * 3.0 + uSeed * 6.28) + 0.06 * sin(a * 5.0 - uSeed * 11.0) + 0.03 * sin(a * 9.0 + uTime * 0.6);
    float edge = 0.86 + wobble;
    if (r > edge) discard;
    float rim = smoothstep(edge - 0.16, edge - 0.02, r);
    float ripple = 0.5 + 0.5 * sin(r * 18.0 - uTime * 1.6 + uSeed * 9.0);
    vec3 col = mix(uColor, uEdge, rim * 0.85 + ripple * 0.12);
    col += uEdge * uGlow * (0.25 + 0.2 * ripple) * (1.0 - r);
    float streak = smoothstep(0.08, 0.0, abs(vUv.x * 0.7 + vUv.y * 0.7 - 0.25 + 0.15 * sin(uTime * 0.4 + uSeed * 5.0)));
    col += uEdge * streak * uGlow * 0.6;
    float alpha = mix(0.82, 0.95, rim) * smoothstep(edge, edge - 0.03, r);
    gl_FragColor = vec4(col, alpha);
  }
`;

interface ChunkView {
  group: Group;
  hazards: Hazard[];
  breakables: Map<string, { obj: Object3D; prop: BreakableProp }>;
}

/**
 * Kałuże i obiekty do rozbicia z silnika (map-features.ts): plamy na ziemi z drobinami nad nimi
 * oraz beczki i skrzynki, które znikają z drzazgami, gdy silnik zgłosi rozbicie.
 */
export class MapFeaturesView {
  private readonly root = new Group();
  private readonly chunks = new Map<string, ChunkView>();
  private readonly geometry = new CircleGeometry(1, 48);
  private readonly materials: ShaderMaterial[] = [];
  private readonly tmp = new Vector3();
  private time = 0;
  private emitCarry = 0;
  private readonly hazardCount: number;
  private readonly breakableCount: number;
  private readonly obstacles: number;

  constructor(
    scene: Scene,
    private readonly assets: Assets,
    private readonly state: GameState,
    private readonly look: ZoneLook,
    private readonly sparks: Particles,
    private readonly smoke: Particles,
  ) {
    const zone = state.content.zones[state.zoneId];
    this.hazardCount = zone?.hazards?.perChunk ?? 0;
    this.breakableCount = look.breakables?.length ? (zone?.breakables ?? 0) : 0;
    this.obstacles = zone?.obstacles ?? 0;
    scene.add(this.root);
  }

  private hazardMaterial(seed: number): ShaderMaterial {
    const kind = this.state.content.zones[this.state.zoneId]!.hazards!.kind;
    const hl = HAZARD_LOOKS[kind];
    const m = new ShaderMaterial({
      vertexShader: hazardVertex,
      fragmentShader: hazardFragment,
      uniforms: {
        uColor: { value: new Color(hl.color) },
        uEdge: { value: new Color(hl.edge) },
        uTime: { value: 0 },
        uSeed: { value: seed },
        uGlow: { value: hl.glow },
      },
      transparent: true,
      depthWrite: false,
    });
    this.materials.push(m);
    return m;
  }

  private buildChunk(cx: number, cy: number): ChunkView {
    const group = new Group();
    const zone = this.state.content.zones[this.state.zoneId]!;
    const hazards = this.hazardCount && zone.hazards ? chunkHazards(this.state.seed, cx, cy, zone.hazards, this.obstacles) : [];
    for (const h of hazards) {
      const mesh = new Mesh(this.geometry, this.hazardMaterial(h.variant));
      mesh.rotation.x = -Math.PI / 2;
      mesh.rotation.z = h.variant * Math.PI * 2;
      mesh.position.set(h.x * S, 0.025 + h.variant * 0.01, h.y * S);
      mesh.scale.setScalar(h.radius * S);
      mesh.renderOrder = 1;
      group.add(mesh);
    }
    const breakables = new Map<string, { obj: Object3D; prop: BreakableProp }>();
    const props = this.look.breakables ?? [];
    for (const b of this.breakableCount ? chunkBreakables(this.state.seed, cx, cy, this.breakableCount, this.obstacles) : []) {
      if (this.state.broken.includes(b.key)) continue;
      const prop = props[Math.floor(b.variant * props.length) % props.length]!;
      const obj = this.assets.template(prop.path).scene.clone();
      obj.position.set(b.x * S, 0, b.y * S);
      obj.rotation.y = b.variant * Math.PI * 14;
      obj.scale.setScalar(prop.scale);
      obj.traverse((o) => {
        o.castShadow = true;
        o.receiveShadow = true;
      });
      group.add(obj);
      breakables.set(b.key, { obj, prop });
    }
    this.root.add(group);
    return { group, hazards, breakables };
  }

  /** Rozbity obiekt: drzazgi w kolorze modelu i obłoczek kurzu. */
  onBroken(x: number, y: number): void {
    for (const view of this.chunks.values()) {
      for (const [key, b] of view.breakables) {
        if (Math.abs(b.obj.position.x - x * S) > 0.01 || Math.abs(b.obj.position.z - y * S) > 0.01) continue;
        b.obj.removeFromParent();
        view.breakables.delete(key);
        const at = this.tmp.set(x * S, 0.5, y * S);
        this.sparks.emit(at, { color: b.prop.chips, count: 22, speed: 4.5, life: 0.9, up: 3.5, gravity: 14, spread: 0.3, size: 0.16, grow: 0.6 });
        this.smoke.emit(at, { color: '#8a7e6e', count: 5, speed: 0.8, life: 1.1, up: 0.6, size: 0.7, grow: 2.2, drag: 1.5, spread: 0.3 });
        return;
      }
    }
  }

  update(dt: number): void {
    this.time += dt;
    for (const m of this.materials) m.uniforms.uTime!.value = this.time;
    if (!this.hazardCount && !this.breakableCount) return;

    const p = this.state.player;
    const pcx = Math.floor(p.x / OBSTACLE_CHUNK);
    const pcy = Math.floor(p.y / OBSTACLE_CHUNK);
    for (let i = -VIEW_CHUNKS; i <= VIEW_CHUNKS; i++) {
      for (let j = -VIEW_CHUNKS; j <= VIEW_CHUNKS; j++) {
        const key = `${pcx + i},${pcy + j}`;
        if (!this.chunks.has(key)) this.chunks.set(key, this.buildChunk(pcx + i, pcy + j));
      }
    }
    for (const [key, view] of this.chunks) {
      const [cx, cy] = key.split(',').map(Number) as [number, number];
      if (Math.abs(cx - pcx) <= VIEW_CHUNKS + 1 && Math.abs(cy - pcy) <= VIEW_CHUNKS + 1) continue;
      view.group.removeFromParent();
      for (const c of view.group.children) if ((c as Mesh).material instanceof ShaderMaterial) this.dropMaterial((c as Mesh).material as ShaderMaterial);
      this.chunks.delete(key);
    }

    // Drobiny nad kałużami blisko bohatera (bąble trucizny, iskry żaru, lodowy pył).
    const zone = this.state.content.zones[this.state.zoneId];
    if (!zone?.hazards) return;
    const hl = HAZARD_LOOKS[zone.hazards.kind];
    this.emitCarry += dt * 10;
    while (this.emitCarry >= 1) {
      this.emitCarry -= 1;
      const list = this.nearHazards(pcx, pcy);
      if (!list.length) break;
      const h = list[Math.floor(Math.random() * list.length)]!;
      const a = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * h.radius * 0.8;
      this.tmp.set((h.x + Math.cos(a) * r) * S, 0.08, (h.y + Math.sin(a) * r) * S);
      this.sparks.emit(this.tmp, { color: hl.particles, count: 1, speed: 0.15, life: 1.4, up: zone.hazards.kind === 'lod' ? 0.2 : 0.7, size: 0.12, grow: 0.3 });
    }
  }

  private nearHazards(pcx: number, pcy: number): Hazard[] {
    const out: Hazard[] = [];
    for (let i = -1; i <= 1; i++) for (let j = -1; j <= 1; j++) out.push(...(this.chunks.get(`${pcx + i},${pcy + j}`)?.hazards ?? []));
    return out;
  }

  private dropMaterial(m: ShaderMaterial): void {
    const i = this.materials.indexOf(m);
    if (i >= 0) this.materials.splice(i, 1);
    m.dispose();
  }

  dispose(): void {
    this.root.removeFromParent();
    for (const m of this.materials) m.dispose();
    this.geometry.dispose();
  }
}
